import Highcharts from "highcharts"
import { Chart } from "highcharts-vue"
import * as R from "remeda"

import boost from "highcharts/modules/boost"
import stockChart from "highcharts/modules/stock"
import exporting from "highcharts/modules/exporting"
import exportData from "highcharts/modules/export-data"
import histogram from "highcharts/modules/histogram-bellcurve"
import More from "highcharts/highcharts-more"
import annotations from "highcharts/modules/annotations"

import type { Options as HighchartOptions } from "highcharts"

const defaultHighchartOptions: HighchartOptions = {
  rangeSelector: {
    buttons: [
      { type: "day", count: 1, text: "1d" },
      { type: "day", count: 15, text: "15d" },
      { type: "month", count: 1, text: "1m" },
      { type: "month", count: 3, text: "3m" },
      { type: "month", count: 5, text: "5m" },
      { type: "ytd", text: "YTD", title: "View year to date" },
      { type: "year", count: 1, text: "1y" },
      { type: "all", text: "All" },
    ],
    inputEnabled: false, // it supports only days
    selected: 7, // all
    buttonTheme: {
      width: 30,
      fill: "none",
      stroke: "none",
      "stroke-width": 0,
      r: 8,
    },
  },
  title: { text: "" },
  credits: { enabled: false },
  accessibility: { enabled: false },
  navigator: {
    enabled: false,
    maskInside: false,
    xAxis: { visible: false },
  },

  scrollbar: { enabled: false },
  responsive: {
    rules: [
      {
        chartOptions: { title: { text: "", floating: true } },
        condition: { maxWidth: 768 },
      },
      {
        condition: { maxWidth: 500 },
        chartOptions: {
          legend: {
            layout: "horizontal",
            align: "center",
            verticalAlign: "bottom",
            floating: false,
            margin: 20,
          },
        },
      },
    ],
  },
  colors: palette,
  chart: {
    // @ts-expect-error : wrongly typed from highchart
    zoomType: "x",
  },
  time: {
    useUTC: false, // display data in browser timezone
  },
  yAxis: { opposite: false },
  xAxis: {
    type: "datetime",
  },
  tooltip: {
    enabled: true,
    animation: true,
    split: true,
    shared: false,
    valueDecimals: 2,
  },
  boost: {
    enabled: true,
    // useGPUTranslations: true,  // unadvised for timestamps xAxis
    usePreallocated: true,
  },
  plotOptions: {
    series: {
      marker: { enabled: false },
      states: { hover: { enabled: true }, select: { enabled: true }, inactive: { opacity: 1 } },
      dataLabels: {
        inside: false,
        allowOverlap: false,
        crop: true,
        // overflow: "none",
        style: {
          color: "#162C62",
          fontSize: "10px",
          fontWeight: "normal",
          textOutline: "none",
        },
        formatter() {
          const value = this.y

          // Don't display labels for NaN
          if (!R.isNumber(value)) {
            return ""
          }

          const nDecimals = 2

          // Don't display labels for near zero values, NaN, or non-numbers
          if (Math.abs(value) < 0.1 ** (nDecimals - 1)) {
            return ""
          }

          // Format with 2 decimals by default
          return value.toLocaleString(undefined, {
            minimumFractionDigits: nDecimals,
            maximumFractionDigits: nDecimals,
          })
        },
      },
      dataGrouping: {
        enabled: true,
        firstAnchor: "start",
        lastAnchor: "start",
        dateTimeLabelFormats: {
          // millisecond: ["%A, %b %e, %H:%M:%S.%L", "%A, %b %e, %H:%M:%S.%L", ""],
          second: ["%A, %b %e, %H:%M", "%A, %b %e, %H:%M", ""],
          minute: ["%A, %b %e, %H:%M", "%A, %b %e, %H:%M", ""],
          hour: ["%A, %b %e, %H:%M", "%A, %b %e, %H:%M", ""],
          day: ["%A, %b %e, %Y", "%A, %b %e", ""],
          week: ["Week from %A, %b %e, %Y", "%A, %b %e", ""],
          month: ["%B %Y", "%B", ""],
          year: ["%Y", "%Y", ""],
        },
      },
    },
    area: {
      marker: {
        enabled: false,
        symbol: "circle",
        radius: 2,
      },
      fillOpacity: 0.5,
      lineWidth: 2,
    },
  },
  legend: {
    enabled: true,
    layout: "vertical",
    align: "right",
    verticalAlign: "middle",
    floating: false,
  },
  exporting: {
    enabled: true,
    sourceWidth: 1800,
    sourceHeight: 700,
  },
}

// highcharts-vue@1.4.0 calls `this.chart.update` in its `options` watcher without checking the chart
// exists. When `options` changes in the same flush that mounts the component, the watcher runs before
// `mounted` created the chart and throws "Cannot read properties of undefined (reading 'update')".
// Skipping the update is safe: `mounted` builds the chart from the latest options anyway.
const chartComponent = Chart as any
const SafeChart = {
  ...chartComponent,
  watch: {
    options: {
      ...chartComponent.watch.options,
      handler(this: { chart?: unknown }, options: HighchartOptions) {
        if (R.isNullish(this.chart)) {
          return
        }

        chartComponent.watch.options.handler.call(this, options)
      },
    },
  },
}

export default defineNuxtPlugin({
  name: "highchart",
  enforce: "pre",
  async setup(nuxtApp) {
    boost(Highcharts)

    exporting(Highcharts)
    exportData(Highcharts)

    histogram(Highcharts)
    stockChart(Highcharts)
    annotations(Highcharts)

    More(Highcharts)

    // Need to be done after registration of highcharts modules:
    Highcharts.setOptions(defaultHighchartOptions)

    Highcharts.removeEvent(Highcharts.Chart, "beforeShowResetZoom") // see https://github.com/highcharts/highcharts/issues/10320#issuecomment-806303951

    // Both tag names are used in templates (`highcharts` was registered by the HighchartsVue plugin).
    nuxtApp.vueApp.component("highchart", SafeChart)
    nuxtApp.vueApp.component("highcharts", SafeChart)
    // this is the equivalent of a normal functional plugin
  },
})
